import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";

export const getPurchasableItems = createAsyncThunk(
  "purchaseOrder/getPurchasableItems",
  async (payload = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      params.append("IsPurchasable", true);
      params.append("IsDiscontinued", true); // for get discontuned item also
      if (payload.searchText) {
        params.append("searchText", payload.searchText);
      }
      params.append("PageNumber", payload.PageNumber || 1);
      params.append("PageSize", payload.PageSize || 100);

      // Add location parameters for location-specific stock filtering
      if (payload.DeliveryLocationId) {
        params.append("DeliveryLocationId", payload.DeliveryLocationId);
      }
      if (payload.DeliveryLocationType) {
        params.append("DeliveryLocationType", payload.DeliveryLocationType);
      }

      const { data } = await axios.get(
        `${API_URL.ITEM.ACTIVE_ITEMS_LOOKUP}?${params.toString()}`,
      );
      return data.data.list;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

// Resolve a SKU/Quantity spreadsheet into priced line items for the items grid.
// Stateless and synchronous — nothing is persisted; the resolved rows are bound
// into the form for review. Distinct from bulkImportPurchaseOrders (whole orders).
export const resolveItemsFromFile = createAsyncThunk(
  "purchaseOrder/resolveItemsFromFile",
  async (
    { file, vendorId, sourceOfSupplyStateId, destinationOfSupplyStateId },
    { rejectWithValue },
  ) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const params = new URLSearchParams();
      if (vendorId) params.append("vendorId", vendorId);
      if (sourceOfSupplyStateId)
        params.append("sourceOfSupplyStateId", sourceOfSupplyStateId);
      if (destinationOfSupplyStateId)
        params.append("destinationOfSupplyStateId", destinationOfSupplyStateId);

      const response = await axios.post(
        `${API_URL.PURCHASE_ORDER.RESOLVE_ITEMS_FROM_FILE}?${params.toString()}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 5 * 60 * 1000, // 5 minutes for large files
        },
      );
      // response.data is the API envelope { data, isSuccess, message };
      // the resolve payload is the inner .data.
      return response.data?.data;
    } catch (error) {
      // Network errors (no response from server)
      if (!error?.data && !error?.response) {
        return rejectWithValue({
          message:
            error?.message ||
            "Network error. The file may be too large or the server timed out.",
        });
      }
      const errorPayload = error?.data?.data || error?.data || error;
      return rejectWithValue(errorPayload);
    }
  },
);

// Fetch stock for multiple items at a specific delivery location
// Used when delivery location changes to update stock for already-selected items
export const getItemsStockByLocation = createAsyncThunk(
  "purchaseOrder/getItemsStockByLocation",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(
        API_URL.STOCK.ITEMS_STOCK_BY_LOCATION,
        payload,
      );
      return data.data; // Returns { itemId1: stock1, itemId2: stock2, ... }
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getNextPONumber = createAsyncThunk(
  "purchaseOrder/getNextPONumber",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.PURCHASE_ORDER.GET_NEXT_PO_NUMBER,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getPurchaseOrders = createAsyncThunk(
  "purchaseOrder/getPurchaseOrders",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      dispatch(setActivePayload(payload));
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.get(
        `${API_URL.PURCHASE_ORDER.SLUG}?${queryString}`,
      );
      const purchase_order_list = data.data.list;
      const totalRecords = data.data.totalRecords;
      return { purchase_order_list, data: { totalRecords } };
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const createPurchaseOrder = createAsyncThunk(
  "purchaseOrder/createPurchaseOrder",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(API_URL.PURCHASE_ORDER.SLUG, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const updatePurchaseOrder = createAsyncThunk(
  "purchaseOrder/updatePurchaseOrder",
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await axios.put(API_URL.PURCHASE_ORDER.SLUG, payload);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const deletePurchaseOrder = createAsyncThunk(
  "purchaseOrder/deletePurchaseOrder",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.delete(
        `${API_URL.PURCHASE_ORDER.SLUG}/${id}`,
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getPurchaseOrderById = createAsyncThunk(
  "purchaseOrder/getPurchaseOrderById",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(`${API_URL.PURCHASE_ORDER.SLUG}/${id}`);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getPurchaseOrderItems = createAsyncThunk(
  "purchaseOrder/getPurchaseOrderItems",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.PURCHASE_ORDER.GET_ITEMS(id));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const submitForApproval = createAsyncThunk(
  "purchaseOrder/submitForApproval",
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.PURCHASE_ORDER.SUBMIT_FOR_APPROVAL}/${id}`,
      );
      if (getState().purchaseOrder.purchase_order_detail?.id === id) {
        dispatch(getPurchaseOrderById(id));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const approvePurchaseOrder = createAsyncThunk(
  "purchaseOrder/approvePurchaseOrder",
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.PURCHASE_ORDER.APPROVE}/${id}`,
      );
      if (getState().purchaseOrder.purchase_order_detail?.id === id) {
        dispatch(getPurchaseOrderById(id));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const rejectPurchaseOrder = createAsyncThunk(
  "purchaseOrder/rejectPurchaseOrder",
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.PURCHASE_ORDER.REJECT}/${id}`,
      );
      if (getState().purchaseOrder.purchase_order_detail?.id === id) {
        dispatch(getPurchaseOrderById(id));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const markAsIssued = createAsyncThunk(
  "purchaseOrder/markAsIssued",
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.PURCHASE_ORDER.MARK_AS_ISSUED}/${id}`,
      );
      if (getState().purchaseOrder.purchase_order_detail?.id === id) {
        dispatch(getPurchaseOrderById(id));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const cancelPurchaseOrder = createAsyncThunk(
  "purchaseOrder/cancelPurchaseOrder",
  async (payload, { rejectWithValue, dispatch, getState }) => {
    try {
      const { id } = payload;
      const { data } = await axios.put(
        `${API_URL.PURCHASE_ORDER.CANCEL}`,
        payload,
      );
      if (getState().purchaseOrder.purchase_order_detail?.id === id) {
        dispatch(getPurchaseOrderById(id));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const reopenPurchaseOrder = createAsyncThunk(
  "purchaseOrder/reopenPurchaseOrder",
  async (id, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.put(
        `${API_URL.PURCHASE_ORDER.REOPEN}/${id}`,
      );
      if (getState().purchaseOrder.purchase_order_detail?.id === id) {
        dispatch(getPurchaseOrderById(id));
      }
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const bulkImportPurchaseOrders = createAsyncThunk(
  "purchaseOrder/bulkImportPurchaseOrders",
  async (
    { file, templateId, businessLocationId, deliveryLocationId },
    { rejectWithValue },
  ) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const params = new URLSearchParams();
      if (templateId) params.append("templateId", templateId);
      if (businessLocationId)
        params.append("businessLocationId", businessLocationId);
      if (deliveryLocationId)
        params.append("deliveryLocationId", deliveryLocationId);

      const queryString = params.toString();
      const url = queryString
        ? `${API_URL.PURCHASE_ORDER.BULK_IMPORT}?${queryString}`
        : API_URL.PURCHASE_ORDER.BULK_IMPORT;

      const data = await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        timeout: 5 * 60 * 1000, // 5 minutes for large files
      });
      // console.log("response", data);
      // // const data = response?.data;
      // if (!data) {
      //   return rejectWithValue({
      //     message: "No response from server. The file may be too large.",
      //     errors: [],
      //   });
      // }

      // // Check for business-level failure (API returns 200 but has validation errors)
      // if (data.data && data.data.errors && data.data.errors.length > 0) {
      //   return rejectWithValue(data.data);
      // }

      return data;
    } catch (error) {
      // Handle network errors (no response from server)
      if (!error?.data && !error?.response) {
        return rejectWithValue({
          message:
            error?.message ||
            "Network error. The file may be too large or the server timed out.",
          errors: [],
        });
      }
      // error.data is the response body: { data: { errors: [...], message: "..." }, isSuccess: true }
      // We need to pass error.data.data to get the inner object with errors
      const errorPayload = error?.data?.data || error?.data || error;
      return rejectWithValue(errorPayload);
    }
  },
);

export const getBulkImportHistory = createAsyncThunk(
  "purchaseOrder/getBulkImportHistory",
  async (payload, { rejectWithValue }) => {
    try {
      const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(
          ([, value]) => value !== null && value !== undefined && value !== "",
        ),
      );
      const params = new URLSearchParams();

      for (const [key, value] of Object.entries(filteredPayload)) {
        if (Array.isArray(value)) {
          value.forEach((item) => {
            params.append(key, item);
          });
        } else {
          params.append(key, value);
        }
      }
      const queryString = params.toString();
      const { data } = await axios.post(
        `${API_URL.PURCHASE_ORDER.BULK_IMPORT_HISTORY}?${queryString}`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const getPurchaseReceivesByPurchaseOrder = createAsyncThunk(
  "purchaseOrder/getPurchaseReceivesByPurchaseOrder",
  async (purchaseOrderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(
        API_URL.PURCHASE_RECEIVE.BY_PURCHASE_ORDER(purchaseOrderId),
      );
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

export const downloadPurchaseOrderPdf = createAsyncThunk(
  "purchaseOrder/downloadPurchaseOrderPdf",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        API_URL.PURCHASE_ORDER.DOWNLOAD_PDF(id),
        {
          headers: {
            Accept: "application/pdf",
          },
          responseType: "blob", // ✅ IMPORTANT
        },
      );
      // Create blob URL from response
      const blob = new Blob([response.data], { type: "application/pdf" });
      const blobUrl = URL.createObjectURL(blob);

      // Open PDF in new tab
      const printWindow = window.open(blobUrl, "_blank");

      // Wait for PDF to load and trigger print dialog
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    } catch (error) {
      return rejectWithValue(error.data);
    }
  },
);

const initialState = {
  purchase_order_list: [],
  purchase_order_detail: null,
  purchase_order_items: [],
  purchasable_items: [],
  current_values: null,
  active_payload: null,
  total_records: 0,
  next_po_number: null,
  bulk_import_history: [],
  purchase_receives_for_po: [],
  isLoading: false,
};

const purchaseOrderSlice = createSlice({
  name: "purchaseOrder",
  initialState,
  reducers: {
    setCurrentValues(state, action) {
      state.current_values = action.payload;
    },
    setActivePayload(state, action) {
      state.active_payload = action.payload;
    },
    resetCurrentValue(state) {
      state.current_values = null;
      state.purchase_order_list = [];
    },
    // Clears only the edit-session seed. Safe to call on AddComponent mount
    // without wiping the cached list used by ListComponent's skip-fetch path.
    resetCurrentValueOnly(state) {
      state.current_values = null;
    },
    resetPurchaseOrderDetail(state) {
      state.purchase_order_detail = null;
    },
    setPurchasableItems(state, action) {
      state.purchasable_items = action.payload;
    },
    resetPurchaseReceivesForPO(state) {
      state.purchase_receives_for_po = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPurchaseOrders.fulfilled, (state, action) => {
        state.purchase_order_list = action.payload.purchase_order_list;
        state.total_records = action.payload.data.totalRecords || 0;
      })
      .addCase(getPurchaseOrderById.fulfilled, (state, action) => {
        state.purchase_order_detail = action.payload;
      })
      .addCase(getPurchaseOrderItems.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getPurchaseOrderItems.fulfilled, (state, action) => {
        state.purchase_order_items = action.payload;
        state.isLoading = false;
      })
      .addCase(getPurchasableItems.fulfilled, (state, action) => {
        const pageNumber = action.meta.arg?.PageNumber || 1;
        if (pageNumber === 1) {
          // Replace items on first page (new filter or initial load)
          state.purchasable_items = action.payload;
        } else {
          // Append for pagination
          state.purchasable_items = [
            ...state.purchasable_items,
            ...action.payload,
          ];
        }
      })
      .addCase(getNextPONumber.fulfilled, (state, action) => {
        state.next_po_number = action.payload;
      })
      .addCase(getBulkImportHistory.fulfilled, (state, action) => {
        state.bulk_import_history = action.payload.data.list || [];
      })
      .addCase(
        getPurchaseReceivesByPurchaseOrder.fulfilled,
        (state, action) => {
          state.purchase_receives_for_po = action.payload || [];
        },
      );
  },
});

export const {
  setCurrentValues,
  setActivePayload,
  resetCurrentValue,
  resetCurrentValueOnly,
  resetPurchaseOrderDetail,
  setPurchasableItems,
  resetPurchaseReceivesForPO,
} = purchaseOrderSlice.actions;
export default purchaseOrderSlice.reducer;
